import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import { formatCurrency } from '../utils/currency';
import { StatementDetail, SellerStatement, formatMonth } from './SellerManagementPage';

interface SellerProfile {
  seller: {
    id: string;
    code: string;
    name: string;
    commissionRate: number;
    status: 'active' | 'inactive';
  };
  email?: string;
  balance: number;
  salesCount: number;
  periods: Array<{
    periodMonth: string;
    totalAmount: number;
    commissionCount: number;
    paid: boolean;
    paidAt?: string;
  }>;
}

export const SellerDashboardPage: React.FC = () => {
  const { user, logout } = useAuth();
  const [openPeriod, setOpenPeriod] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'UpCar Vendedor';
    return () => {
      document.title = 'UpCar Aspiradores';
    };
  }, []);

  const meQuery = useQuery({
    queryKey: ['seller-me'],
    queryFn: async () => {
      const res = await api.get('/seller/me');
      return res.data as SellerProfile;
    },
    // A sale confirmed while the seller is standing at the machine should show up
    // on its own, without them having to reload the page.
    refetchInterval: 30000,
  });

  const statementQuery = useQuery({
    queryKey: ['seller-statement', openPeriod],
    queryFn: async () => {
      const res = await api.get('/seller/statement', { params: { periodMonth: openPeriod } });
      return res.data as SellerStatement;
    },
    enabled: !!openPeriod,
  });

  const profile = meQuery.data;
  const periods = profile?.periods || [];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-gradient-to-r from-orange-600 to-orange-500 text-white shadow">
        <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold">Painel do Vendedor</h1>
            <p className="text-sm text-orange-100">{user?.name}</p>
          </div>
          <button
            onClick={logout}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-medium"
          >
            Sair
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {meQuery.isLoading ? (
          <p className="text-gray-500">Carregando…</p>
        ) : meQuery.error || !profile ? (
          <p className="text-red-600">Não foi possível carregar seus dados.</p>
        ) : (
          <>
            <section className="bg-white rounded-xl shadow p-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-orange-50 border border-orange-200 text-center">
                  <div className="text-xs text-gray-500">Seu código de vendas</div>
                  <div className="font-mono text-3xl font-bold tracking-widest text-orange-700 mt-1">
                    {profile.seller.code}
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-gray-50">
                  <div className="text-xs text-gray-500">Saldo a receber</div>
                  <div className="text-3xl font-bold text-gray-900 mt-1">
                    {formatCurrency(profile.balance)}
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-gray-50">
                  <div className="text-xs text-gray-500">Vendas não pagas</div>
                  <div className="text-3xl font-bold text-gray-900 mt-1">
                    {profile.salesCount.toLocaleString('pt-BR')}
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4">
                Você recebe{' '}
                {profile.seller.commissionRate.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%
                do valor de cada primeira assinatura feita com o seu código. O saldo é zerado quando
                o pagamento do mês é registrado.
              </p>
              {profile.seller.status !== 'active' && (
                <p className="text-sm text-red-600 mt-2 font-medium">
                  Seu código está inativo no momento — novas assinaturas não serão contabilizadas.
                </p>
              )}
            </section>

            <section className="bg-white rounded-xl shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Recibos mensais</h2>
              {periods.length === 0 ? (
                <p className="text-gray-500">Você ainda não tem vendas registradas.</p>
              ) : (
                <ul className="space-y-3">
                  {periods.map((p) => (
                    <li key={p.periodMonth} className="border rounded-lg overflow-hidden">
                      <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-3 bg-gray-50">
                        <div>
                          <div className="font-semibold capitalize">{formatMonth(p.periodMonth)}</div>
                          <div className="text-sm text-gray-600">
                            {p.commissionCount.toLocaleString('pt-BR')} venda
                            {p.commissionCount === 1 ? '' : 's'} •{' '}
                            <span className="font-bold text-gray-900">
                              {formatCurrency(p.totalAmount)}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              p.paid ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}
                          >
                            {p.paid
                              ? `Pago${p.paidAt ? ` em ${new Date(p.paidAt).toLocaleDateString('pt-BR')}` : ''}`
                              : 'A receber'}
                          </span>
                          <button
                            onClick={() =>
                              setOpenPeriod(openPeriod === p.periodMonth ? null : p.periodMonth)
                            }
                            className="px-3 py-1.5 border border-gray-300 text-sm rounded-lg hover:bg-white"
                          >
                            {openPeriod === p.periodMonth ? 'Fechar' : 'Ver recibo'}
                          </button>
                        </div>
                      </div>

                      {openPeriod === p.periodMonth && (
                        <div className="px-4 py-3 border-t">
                          {statementQuery.isLoading ? (
                            <p className="text-sm text-gray-500">Carregando recibo…</p>
                          ) : (
                            <StatementDetail statement={statementQuery.data} />
                          )}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
};
